import type {
    CommerceCatalogueExperience,
    CommerceOperationOptions,
    CommerceProduct,
    CommerceProductIdentity,
    CommerceProductCatalogueRepository,
    CommerceProductCategory,
    SearchCommerceProductsRequest,
    SearchCommerceProductsResponse,
} from "@teqtank/commerce-contracts";
import { CommerceError } from "../errors/commerce-error.js";
import { getCommerceProductCategoriesOperation } from "../operations/get-commerce-product-categories-operation.js";
import { getCommerceProductCategoryOperation } from "../operations/get-commerce-product-category-operation.js";
import { getCommerceProductOperation } from "../operations/get-commerce-product-operation.js";
import { searchCommerceProductsOperation } from "../operations/search-commerce-products-operation.js";
import {
    createProductCacheKey,
    createProductCategoriesCacheKey,
    createProductCategoryCacheKey,
    createProductSearchCacheKey,
} from "./product-cache-key.js";
import { ProductCache } from "./product-cache.js";
import {
    normalizeCatalogueExperience,
    normalizeProductSearchRequest,
    requireProductIdentifier,
} from "./product-request-normalizer.js";
import {
    resolveProductCategoriesResponse,
    resolveProductCategoryResponse,
    resolveProductResponse,
    resolveProductSearchResponse,
} from "./product-response-resolver.js";

import { CommerceOperationExecutor } from "../execution/commerce-operation-executor.js";

export class ProductCatalogueRepository implements CommerceProductCatalogueRepository {
    private readonly cache = new ProductCache();
    private readonly pending = new Map<string, Promise<unknown>>();
    private cacheGeneration = 0;

    public constructor(private readonly executor: CommerceOperationExecutor) {}

    public async search(
        request: SearchCommerceProductsRequest = {},
        options: CommerceOperationOptions = {},
    ): Promise<SearchCommerceProductsResponse> {
        const normalizedRequest = normalizeProductSearchRequest(request);

        const cacheKey = createProductSearchCacheKey(normalizedRequest);
        const cacheGeneration = this.cacheGeneration;

        if (options.bypassCache !== true) {
            const cached = this.cache.get<SearchCommerceProductsResponse>(cacheKey);

            if (cached !== undefined) {
                return cached;
            }
        }

        return await this.coalesce(cacheKey, options, async () => {
            try {
                const response = await this.executor.execute(
                    searchCommerceProductsOperation,
                    normalizedRequest,
                    options,
                    "product-search-failed",
                    "Teqtank Commerce could not search the Product catalogue.",
                );

                return this.cacheIfCurrent(
                    cacheKey,
                    resolveProductSearchResponse(response),
                    cacheGeneration,
                );
            } catch (cause) {
                if (cause instanceof CommerceError) {
                    throw cause;
                }

                throw new CommerceError(
                    "product-search-failed",
                    "Teqtank Commerce could not search the Product catalogue.",
                    {
                        cause,
                    },
                );
            }
        });
    }

    public get(
        identifier: string,
        identity: CommerceProductIdentity = "id",
        options: CommerceOperationOptions = {},
    ): Promise<CommerceProduct> {
        return this.getScoped(identifier, identity, options, "shop");
    }

    private async getScoped(
        identifier: string,
        identity: CommerceProductIdentity,
        options: CommerceOperationOptions,
        experience: CommerceCatalogueExperience,
        catalogueId?: string,
    ): Promise<CommerceProduct> {
        const normalizedIdentifier = requireProductIdentifier(identifier, `Product ${identity}`);
        const normalizedCatalogueId =
            catalogueId === undefined
                ? undefined
                : requireProductIdentifier(catalogueId, "Product catalogue ID");
        const normalizedExperience = normalizeCatalogueExperience(experience);
        const cacheKey = createProductCacheKey(
            normalizedIdentifier,
            identity,
            normalizedExperience,
            normalizedCatalogueId,
        );
        const cacheGeneration = this.cacheGeneration;

        if (options.bypassCache !== true) {
            const cached = this.cache.get<CommerceProduct>(cacheKey);

            if (cached !== undefined) {
                return cached;
            }
        }

        return this.coalesce(cacheKey, options, async () => {
            try {
                const response = await this.executor.execute(
                    getCommerceProductOperation,
                    {
                        identifier: normalizedIdentifier,
                        identity,
                        experience: normalizedExperience,
                        ...(normalizedCatalogueId === undefined
                            ? {}
                            : { catalogueId: normalizedCatalogueId }),
                    },
                    options,
                    "product-search-failed",
                    `Teqtank Commerce could not retrieve Product '${normalizedIdentifier}'.`,
                    { identifier: normalizedIdentifier, identity },
                );

                const product = resolveProductResponse(response);

                if (product === null) {
                    throw new CommerceError(
                        "product-not-found",
                        `Product '${normalizedIdentifier}' was not found.`,
                        { identifier: normalizedIdentifier, identity },
                    );
                }

                return this.cacheIfCurrent(cacheKey, product, cacheGeneration);
            } catch (cause) {
                if (cause instanceof CommerceError) {
                    throw cause;
                }

                throw new CommerceError(
                    "product-search-failed",
                    `Teqtank Commerce could not retrieve Product '${normalizedIdentifier}'.`,
                    { identifier: normalizedIdentifier, identity, cause },
                );
            }
        });
    }

    public getById(
        productId: string,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceProduct> {
        return this.get(productId, "id", options);
    }

    public getBySlug(
        slug: string,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceProduct> {
        return this.get(slug, "slug", options);
    }

    public getBySku(sku: string, options: CommerceOperationOptions = {}): Promise<CommerceProduct> {
        return this.get(sku, "sku", options);
    }

    public getByIdInExperience(
        productId: string,
        experience: CommerceCatalogueExperience,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceProduct> {
        return this.getScoped(productId, "id", options, experience);
    }

    public getByIdInCatalogue(
        catalogueId: string,
        productId: string,
        experience: CommerceCatalogueExperience,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceProduct> {
        return this.getScoped(productId, "id", options, experience, catalogueId);
    }

    public async getCategoryById(
        categoryId: string,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceProductCategory> {
        return this.getCategoryByIdScoped(categoryId, options, "shop");
    }

    public async getCategoryByIdInCatalogue(
        catalogueId: string,
        categoryId: string,
        experience: CommerceCatalogueExperience,
        options: CommerceOperationOptions = {},
    ): Promise<CommerceProductCategory> {
        return this.getCategoryByIdScoped(categoryId, options, experience, catalogueId);
    }

    private async getCategoryByIdScoped(
        categoryId: string,
        options: CommerceOperationOptions,
        experience: CommerceCatalogueExperience,
        catalogueId?: string,
    ): Promise<CommerceProductCategory> {
        const normalizedCategoryId = requireProductIdentifier(categoryId, "Product Category ID");
        const normalizedExperience = normalizeCatalogueExperience(experience);
        const normalizedCatalogueId =
            catalogueId === undefined
                ? undefined
                : requireProductIdentifier(catalogueId, "Product catalogue ID");

        const cacheKey = createProductCategoryCacheKey(
            normalizedCategoryId,
            normalizedExperience,
            normalizedCatalogueId,
        );
        const cacheGeneration = this.cacheGeneration;

        if (options.bypassCache !== true) {
            const cached = this.cache.get<CommerceProductCategory>(cacheKey);

            if (cached !== undefined) {
                return cached;
            }
        }

        return this.coalesce(cacheKey, options, async () => {
            try {
                const response = await this.executor.execute(
                    getCommerceProductCategoryOperation,
                    {
                        categoryId: normalizedCategoryId,
                        experience: normalizedExperience,
                        ...(normalizedCatalogueId === undefined
                            ? {}
                            : { catalogueId: normalizedCatalogueId }),
                    },
                    options,
                    "product-search-failed",
                    `Teqtank Commerce could not retrieve Product Category '${normalizedCategoryId}'.`,
                    { categoryId: normalizedCategoryId },
                );

                const category = resolveProductCategoryResponse(response);

                if (category === null) {
                    throw new CommerceError(
                        "product-category-not-found",
                        `Product Category '${normalizedCategoryId}' was not found.`,
                        {
                            categoryId: normalizedCategoryId,
                        },
                    );
                }

                return this.cacheIfCurrent(cacheKey, category, cacheGeneration);
            } catch (cause) {
                if (cause instanceof CommerceError) {
                    throw cause;
                }

                throw new CommerceError(
                    "product-search-failed",
                    `Teqtank Commerce could not retrieve Product Category '${normalizedCategoryId}'.`,
                    {
                        categoryId: normalizedCategoryId,
                        cause,
                    },
                );
            }
        });
    }

    public async getCategories(
        parentId?: string | null,
        options: CommerceOperationOptions = {},
    ): Promise<readonly CommerceProductCategory[]> {
        return this.getCategoriesScoped(parentId, options, "shop");
    }

    private async getCategoriesScoped(
        parentId: string | null | undefined,
        options: CommerceOperationOptions,
        experience: CommerceCatalogueExperience,
        catalogueId?: string,
    ): Promise<readonly CommerceProductCategory[]> {
        const normalizedParentId =
            parentId === null || parentId === undefined
                ? parentId
                : requireProductIdentifier(parentId, "Parent Product Category ID");

        const normalizedCatalogueId =
            catalogueId === undefined
                ? undefined
                : requireProductIdentifier(catalogueId, "Product catalogue ID");
        const normalizedExperience = normalizeCatalogueExperience(experience);
        const cacheKey = createProductCategoriesCacheKey(
            normalizedParentId,
            normalizedExperience,
            normalizedCatalogueId,
        );
        const cacheGeneration = this.cacheGeneration;

        if (options.bypassCache !== true) {
            const cached = this.cache.get<readonly CommerceProductCategory[]>(cacheKey);

            if (cached !== undefined) {
                return cached;
            }
        }

        return this.coalesce(cacheKey, options, async () => {
            try {
                const request = {
                    experience: normalizedExperience,
                    ...(normalizedParentId === undefined ? {} : { parentId: normalizedParentId }),
                    ...(normalizedCatalogueId === undefined
                        ? {}
                        : { catalogueId: normalizedCatalogueId }),
                };

                const response = await this.executor.execute(
                    getCommerceProductCategoriesOperation,
                    request,
                    options,
                    "product-search-failed",
                    "Teqtank Commerce could not retrieve Product Categories.",
                    { parentId: normalizedParentId },
                );

                return this.cacheIfCurrent(
                    cacheKey,
                    resolveProductCategoriesResponse(response),
                    cacheGeneration,
                );
            } catch (cause) {
                if (cause instanceof CommerceError) {
                    throw cause;
                }

                throw new CommerceError(
                    "product-search-failed",
                    "Teqtank Commerce could not retrieve Product Categories.",
                    {
                        parentId: normalizedParentId,
                        cause,
                    },
                );
            }
        });
    }

    public getCategoriesInCatalogue(
        catalogueId: string,
        experience: CommerceCatalogueExperience,
        parentId?: string | null,
        options: CommerceOperationOptions = {},
    ): Promise<readonly CommerceProductCategory[]> {
        return this.getCategoriesScoped(parentId, options, experience, catalogueId);
    }

    public clearCache(): void {
        this.cacheGeneration++;
        this.cache.clear();
        this.pending.clear();
    }

    private cacheIfCurrent<T>(cacheKey: string, value: T, generation: number): T {
        return generation === this.cacheGeneration ? this.cache.set(cacheKey, value) : value;
    }

    private coalesce<T>(
        cacheKey: string,
        options: CommerceOperationOptions,
        execute: () => Promise<T>,
    ): Promise<T> {
        if (options.bypassCache === true || options.signal !== undefined) {
            return execute();
        }

        const existing = this.pending.get(cacheKey) as Promise<T> | undefined;
        if (existing !== undefined) {
            return existing;
        }

        const pending = execute();
        this.pending.set(cacheKey, pending);
        const clear = (): void => {
            if (this.pending.get(cacheKey) === pending) {
                this.pending.delete(cacheKey);
            }
        };
        void pending.then(clear, clear);
        return pending;
    }
}
